import { create } from "zustand";
import { persist } from "zustand/middleware";

/** `normal` is `--article-max-width`, the column print and PDF export use; a number is pixels. */
export type ArticleWidth = "normal" | "full" | number;

export const ARTICLE_WIDTH_STORAGE_KEY = "ArticleWidth";
export const ARTICLE_SCREEN_WIDTH_VAR = "--article-screen-width";

const SNAP_PX = 16;

interface ArticleWidthState {
	width: ArticleWidth;
	setWidth: (width: ArticleWidth) => void;
}

export const useArticleWidthStore = create<ArticleWidthState>()(
	persist(
		(set) => ({
			width: "normal",
			setWidth: (width) => set({ width }),
		}),
		{
			name: ARTICLE_WIDTH_STORAGE_KEY,
			partialize: ({ width }) => ({ width }),
		},
	),
);

export const resolveArticleWidth = (width: ArticleWidth, normal: number, available: number): number => {
	if (width === "normal") return normal;
	if (width === "full") return available;
	return Math.min(Math.max(width, normal), available);
};

export const snapArticleWidth = (px: number, normal: number, available: number): ArticleWidth => {
	if (px - normal < SNAP_PX) return "normal";
	if (available - px < SNAP_PX) return "full";
	return Math.round(px);
};

export const toggleArticleWidth = (current: number, normal: number): ArticleWidth =>
	current > normal ? "normal" : "full";

const toScreenWidth = (width: ArticleWidth): string | null => {
	if (width === "full") return "100%";
	if (typeof width === "number" && Number.isFinite(width) && width > 0) return `${Math.round(width)}px`;
	return null;
};

export const applyArticleScreenWidth = (width: ArticleWidth) => {
	const value = toScreenWidth(width);
	const style = document.documentElement.style;
	if (value) style.setProperty(ARTICLE_SCREEN_WIDTH_VAR, value);
	else style.removeProperty(ARTICLE_SCREEN_WIDTH_VAR);
};

export const ARTICLE_WIDTH_BOOTSTRAP_SCRIPT = `try {
	const width = JSON.parse(localStorage.getItem("${ARTICLE_WIDTH_STORAGE_KEY}"))?.state?.width;
	const value = width === "full" ? "100%" : Number.isFinite(width) && width > 0 ? Math.round(width) + "px" : "";
	if (value) document.documentElement.style.setProperty("${ARTICLE_SCREEN_WIDTH_VAR}", value);
} catch {}`;
